import React from 'react';
import { PricingDisplayMode } from 'src/elements/pricing/PricingBanner';
import Heading from 'src/elements/typography/heading';
import Paragraph from 'src/elements/typography/paragraph';
import CheckedItem from 'src/molecules/checklist/checked.item';
import {
  HintContainer,
  PricingDetailsTableContainer,
  PricingValue,
  TableCellDetailContentExplain,
  TableCellHeadPlanDetail,
  TableCellRowTitle,
  TablePlanCell,
  TableRowPlanDetail,
  TableRowTableTileHead,
  TableTitle,
} from './PricingDetailsTable.styles';

import UnlimitedTag from 'src/molecules/tags/tag.unlimited';
import MightyExclusive from 'src/molecules/tags/tag.mightyExclusive';

interface TableCellPlanProps {
  unlimited?: boolean;
  checked?: boolean;
  text?: string;
}

interface TableRowProps {
  title: string;
  subtitle: string;
  mightyExclusive?: boolean;
  plans: TableCellPlanProps[]
}

interface PricesData {
  annual?: number;
  monthly?: number;
}

export interface PricingDetailsTableProps {
  activePriceMode: PricingDisplayMode;
  mightyExclusive?: boolean;
  addPrices?: boolean;
  hint?: string;
  title: string;
  rows: TableRowProps[];
  prices: {
    community: PricesData;
    business: PricesData;
  }
}

interface PricesProps {
  activePriceMode: PricingDisplayMode;
  prices: {
    community: PricesData;
    business: PricesData;
  }
}

const Prices = ({ activePriceMode, prices }: PricesProps): JSX.Element => (
  <TableRowPlanDetail>
    <th />
    <th align="center">
      <TableCellHeadPlanDetail>
        <Heading
          text="Community Plan"
          size="18px"
          sizeSm="14px"
          align="center"
        />
        <PricingValue
          text={String(activePriceMode === 'annual' ? prices.community.annual : prices.community.monthly)}
          size="32px"
          sizeSm="14px"
          align="center"
        />
      </TableCellHeadPlanDetail>
    </th>
    <th align="center">
      <TableCellHeadPlanDetail>
        <Heading
          text="Business Plan"
          size="18px"
          sizeSm="14px"
          align="center"
        />
        <PricingValue
          text={String(activePriceMode === 'annual' ? prices.business.annual : prices.business.monthly)}
          size="32px"
          sizeSm="14px"
          align="center"
        />
      </TableCellHeadPlanDetail>
    </th>
  </TableRowPlanDetail>
)

const TableCellRenderer = ({ unlimited, checked, text }: TableCellPlanProps): JSX.Element => {
  if (unlimited)
    return (
      <TablePlanCell align="center">
        <UnlimitedTag />
      </TablePlanCell>
    );
  else if (checked)
    return (
      <TablePlanCell align="center">
        <CheckedItem checked />
      </TablePlanCell>
    );
  else if (text)
    return (
      <TablePlanCell align="center">
        <Paragraph color="#4D5461" text={text} size="18px" sizeSm="14px" weight="light" align="center"/>
      </TablePlanCell>
    );

  return <TablePlanCell align="center"/>;
}

const TableRowRenderer = ({ title, subtitle, mightyExclusive, plans }: TableRowProps): JSX.Element => (
  <tr>
    <TableCellRowTitle align="left">
      <TableCellDetailContentExplain>
        <Paragraph
          size="1rem"
          sizeSm="0.9rem"
          align="left"
          color="#000000"
          weight="normal"
        >
          {title}{ mightyExclusive && <MightyExclusive /> }
        </ Paragraph>
        <Paragraph
          text={subtitle}
          size="1rem"
          align="left"
          color="#6F7682"
          weight="light"
        />
      </TableCellDetailContentExplain>
    </TableCellRowTitle>
    {
      plans.map((plan, planIndex) => <TableCellRenderer {...plan} key={planIndex} />)
    }
  </tr>
)

const PricingDetailsTable = ({
  addPrices,
  mightyExclusive,
  title,
  rows,
  activePriceMode,
  hint,
  prices
}: PricingDetailsTableProps): JSX.Element => {
  return (
    <React.Fragment>
      <PricingDetailsTableContainer>
        <thead>
          {addPrices && <Prices prices={prices} activePriceMode={activePriceMode} />}
          <TableRowTableTileHead>
            <td>
              <TableTitle>
                <Heading text={title} size="28px" sizeMd="18px" align="left" />
                {mightyExclusive && <MightyExclusive />}
              </TableTitle>
            </td>
            <td />
            <td />
          </TableRowTableTileHead>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <TableRowRenderer {...row} key={index} />
          ))}
        </tbody>
      </PricingDetailsTableContainer>
      {
        hint && (
          <HintContainer>
            <Paragraph
              text={hint}
              size="1rem"
              sizeSm="0.84rem"
              weight="light"
              color="#6F7682"
              align="left"
            />
          </HintContainer>
        )
      }
    </React.Fragment>
  );
};

export default PricingDetailsTable;
